import { Tab, Tabs } from '@mui/material';
import React, { useState } from 'react';
import { useNotification } from '../../hooks/NotificationContext';
import { inviteuser, inviteUsersBulk } from '../../modules/users';
import Modal from '../elements/Modal';
import AddUserForm from './AddUserForm';
import BulkInviteUser from './BulkInviteUser';
import { Spinner } from 'react-bootstrap';
import { useInvalidateQuery } from '../../query/apiQueries';
import JiraUserMappingTableModal from '../integrations/JiraUserMappingPage';
import { useQueryClient } from '@tanstack/react-query';

export default function AddUser({ fetchUsersData, open, setOpen }) {
  const { addNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [inviteResults, setInviteResults] = useState(null);
  const [mappingProjects, setMappingProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const queryClient = useQueryClient();

  const invalidateQueries = useInvalidateQuery();

  const handleSingleInvite = async (values) => {
    const { projects = [], ...rest } = values;
    const userPayload = {
      ...rest,
      projects,
      terms_conditions: true,
      platform: 'web',
      password: process.env.REACT_APP_PASSWORD_SECRET_KEY || 'invited_user',
    };

    setLoading(true);
    try {
      await inviteuser(userPayload);
      addNotification(
        'success',
        'An invite has been sent to the user! They will receive an email shortly.'
      );
      fetchUsersData();
      invalidateQueries([{ key: 'user', type: 'list' }]);
      setOpen(false);

      const jiraProjects = projects.filter(p => p.jiraCloudId);
      if (jiraProjects.length > 0) {
        const cloudGroups = {};
        jiraProjects.forEach(p => {
          if (!cloudGroups[p.jiraCloudId]) cloudGroups[p.jiraCloudId] = [];
          cloudGroups[p.jiraCloudId].push(p);
        });
        const groupedProjects = Object.values(cloudGroups).map(group => group[0]);
        setMappingProjects(groupedProjects);
        setCurrentProject(groupedProjects[0]);
        groupedProjects.forEach(p =>
          queryClient.invalidateQueries({
            queryKey: ['jira-mappings', p.id],
          })
        );
      }

    } catch (error) {
      addNotification(
        'error',
        error.message || 'Unable to invite user.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBulkInvite = async (users) => {
    setLoading(true);
    setInviteResults(null);
    try {
      const response = await inviteUsersBulk(users);
      setInviteResults(response.results);
      if (response.results.invited.length > 0) {
        addNotification(
          'success',
          'Invites have been sent to the users! They will receive an email shortly.'
        );
      }
      fetchUsersData();
      invalidateQueries([{ key: 'user', type: 'list' }]);
    } catch (error) {
      addNotification('error', error.message || 'Bulk invite failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setOpen(false);
    setTab(0);
    setInviteResults(null);
    setLoading(false);
  };

  const handleMappingClose = () => {
    const remaining = mappingProjects.slice(1);
    if (remaining.length > 0) {
      setMappingProjects(remaining);
      setCurrentProject(remaining[0]);
    } else {
      setMappingProjects([]);
      setCurrentProject(null);
    }
  };

  return (
    <>
      <Modal
        open={open}
        modalTitle="Invite Members"
        handleClose={handleModalClose}
        width="sm">
        <Tabs
          value={tab}
          onChange={(_, newValue) => setTab(newValue)}
          sx={{ mb: 3 }}>
          <Tab label="Add User" />
          <Tab label="Bulk Invite" />
        </Tabs>
        {loading ? (
          <div style={{ textAlign: 'center' }}>
            <Spinner animation="border" />
            <p>Inviting...</p>
          </div>
        ) : tab === 0 ? (
          <AddUserForm handleSubmit={handleSingleInvite} loading={loading} />
        ) : (
          <BulkInviteUser
            handleSubmit={handleBulkInvite}
            loading={loading}
            inviteResults={inviteResults}
          />
        )}
      </Modal>
      {currentProject && (
        <JiraUserMappingTableModal
          project={currentProject}
          open={!!currentProject}
          handleClose={handleMappingClose}
          isAdmin={true}
          isAddUserFlow={true}
        />
      )}
    </>
  );
}
